// components/CreatePortsOrderModal.jsx
import React, { useState, useEffect } from "react";
import { X, Building2, Briefcase } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreatePortsOrderModal = ({ project, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [formData, setFormData] = useState({
    projectId: project?.id || "",
    accountId: project?.accountId || "",
    portsOrderName: "",
    numbersToPort: "",
    requestedDate: "",
    acceptedDate: "",
    portCompleted: false,
  });

  useEffect(() => {
    if (!project) {
      fetchProjects();
      fetchAccounts();
    }
  }, [project]);

  const fetchProjects = async () => {
    try {
      const response = await apiService.getProjects({ limit: 1000 });
      setProjects(response.data || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await apiService.getAccounts({ limit: 1000 });
      setAccounts(response.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const handleProjectChange = (projectId) => {
    const selectedProject = projects.find((p) => p.id === projectId);
    setFormData({
      ...formData,
      projectId,
      accountId: selectedProject?.accountId || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = { ...formData };

      // Convert empty strings to null
      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === "") {
          submitData[key] = null;
        }
      });

      await apiService.createPortsOrder(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating ports order:", err);
      setError(err.response?.data?.message || "Failed to create ports order");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            New Ports Order
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/20 border border-red-500/30 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="portsOrderForm">
            {/* Required Fields */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Ports Order Information
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              {/* Project */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <span className="text-red-500">*</span> Project
                </label>
                <div className="col-span-2">
                  {project ? (
                    <div
                      className={`flex items-center gap-2 px-3 py-2 border rounded ${
                        isDarkMode
                          ? "bg-blue-500/20 border-blue-500/30"
                          : "bg-blue-50 border-blue-200"
                      }`}
                    >
                      <Briefcase
                        className={`w-4 h-4 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {project.projectName}
                      </span>
                    </div>
                  ) : (
                    <select
                      value={formData.projectId}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select Project</option>
                      {projects.map((proj) => (
                        <option key={proj.id} value={proj.id}>
                          {proj.projectName} ({proj.projectNumber})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Account (auto-filled from project) */}
              {formData.accountId && (
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Account
                  </label>
                  <div className="col-span-2">
                    <div
                      className={`flex items-center gap-2 px-3 py-2 border rounded ${
                        isDarkMode
                          ? "bg-gray-800/50 border-gray-700"
                          : "bg-gray-50 border-gray-300"
                      }`}
                    >
                      <Building2
                        className={`w-4 h-4 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      />
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        {project?.account?.name ||
                          accounts.find((a) => a.id === formData.accountId)
                            ?.name}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Ports Order Name */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Ports Order Name
                </label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.portsOrderName}
                    onChange={(e) =>
                      handleChange("portsOrderName", e.target.value)
                    }
                    placeholder="Enter ports order name"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>

              {/* Numbers to Port */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Numbers To Port
                </label>
                <div className="col-span-2">
                  <textarea
                    rows="4"
                    value={formData.numbersToPort}
                    onChange={(e) =>
                      handleChange("numbersToPort", e.target.value)
                    }
                    placeholder="Enter phone numbers to port (one per line or comma-separated)"
                    className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                  <div
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    Enter phone numbers separated by commas or line breaks
                  </div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Important Dates
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              {/* Requested Date */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Requested Date
                </label>
                <div className="col-span-2">
                  <input
                    type="date"
                    value={formData.requestedDate}
                    onChange={(e) =>
                      handleChange("requestedDate", e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              {/* Accepted Date */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Accepted Date
                </label>
                <div className="col-span-2">
                  <input
                    type="date"
                    value={formData.acceptedDate}
                    onChange={(e) =>
                      handleChange("acceptedDate", e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Status
              </h3>
            </div>

            <div className="mb-6">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.portCompleted}
                  onChange={(e) =>
                    handleChange("portCompleted", e.target.checked)
                  }
                  className={`w-4 h-4 rounded border text-blue-600 focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-700 bg-[#0C0E12]"
                      : "border-gray-300 bg-white"
                  }`}
                />
                Port Completed
              </label>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="portsOrderForm"
            disabled={loading || !formData.projectId}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePortsOrderModal;
